import type { PageSection, PageSectionWithLocale } from '@/hooks/useLpg';
import { type Config as CarTemplateConfig, carPpmTemplateConfig, carTemplateConfig } from '@/pageConfig/car';
import { type Config as HomeTemplateConfig, homeTemplateConfig } from '@/pageConfig/home';
import { type Config as HomeownersTemplateConfig, homeownersTemplateConfig } from '@/pageConfig/homeowners';
import { type Config as LifeTemplateConfig, lifeTemplateConfig } from '@/pageConfig/life';
import { type Config as PetTemplateConfig, petTemplateConfig } from '@/pageConfig/pet/pet';
import { type Config as RentersTemplateConfig, rentersTemplateConfig } from '@/pageConfig/renters';
import type { Locale } from '@lemonade-hq/lemonation';
import type { ProductsByHeroSection, SectionsType, StaticComponentProps } from './lpgTypesAndDefinitions';

// extend this type with more products config types
type TemplatesConfigByLocale =
  | Record<Locale, HomeTemplateConfig>
  | Record<Locale, RentersTemplateConfig>
  | Record<Locale, PetTemplateConfig>
  | Record<Locale, HomeownersTemplateConfig>
  | Record<Locale, LifeTemplateConfig>
  | Record<Locale, CarTemplateConfig>;

const CONFIG_BY_PRODUCT_TEMPLATE = {
  default: homeTemplateConfig,
  renters: rentersTemplateConfig,
  pet: petTemplateConfig,
  home: homeownersTemplateConfig,
  life: lifeTemplateConfig,
  car: carTemplateConfig,
  // Extend LPG config: add more product and config maps by locale here
} as Record<ProductsByHeroSection | 'default', TemplatesConfigByLocale>;

const productPageSections = {
  hero: ['renters_hero', 'pet_hero', 'ho_hero', 'life_hero', 'car_hero'],
  reviews: ['reviews', 'renters_reviews', 'ho_reviews', 'car_reviews'],
  product: ['products', 'renters_products', 'ho_products'],
} as Record<string, SectionsType[]>;

export const getExtraPropsBySectionType = ({
  type,
  props,
  locale,
  product,
  isCarPpm,
}: PageSectionWithLocale & {
  product: ProductsByHeroSection | undefined;
  isCarPpm: boolean;
}): StaticComponentProps & Pick<PageSection, 'props'> => {
  // this is only need it for those sections with props in their config that are not present in from lpg data
  const configByLocale = isCarPpm
    ? carPpmTemplateConfig[locale]
    : CONFIG_BY_PRODUCT_TEMPLATE[product ?? 'default'][locale];

  const isCustomerReviews = productPageSections.reviews.includes(type);
  const isProducts = productPageSections.product.includes(type);
  const isProductPageHero = productPageSections.hero.includes(type);

  const fallbackConfigForCar = product !== 'car' ? CONFIG_BY_PRODUCT_TEMPLATE['car'][locale] : configByLocale;

  if (isCustomerReviews) {
    if ('customersReviews' in configByLocale)
      return {
        ...configByLocale.customersReviews,
        ...props,
      };

    return {};
  }

  if (isProducts) {
    if ('products' in configByLocale)
      return {
        ...configByLocale.products,
        ...props,
      };

    return {};
  }

  if (type === 'instant') {
    if ('instantEverything' in configByLocale)
      return {
        ...configByLocale.instantEverything,
        ...props,
      };

    return {};
  }

  if (type === 'switching') {
    if ('switching' in configByLocale)
      return {
        ...configByLocale.switching,
        ...props,
      };

    return {};
  }

  if (type === 'how_it_works') {
    if ('howItWorks' in configByLocale)
      return {
        ...configByLocale.howItWorks,
        ...props,
      };

    return {};
  }

  if (type === 'trust') {
    if ('trust' in configByLocale)
      return {
        ...configByLocale.trust,
        ...props,
      };

    return {};
  }

  if (type === 'eu_products') {
    if ('genericProducts' in configByLocale)
      return {
        ...configByLocale.genericProducts,
        ...props,
      };

    return {};
  }

  if (type === 'eu_switching') {
    if ('switchingEurope' in configByLocale)
      return {
        ...configByLocale.switchingEurope,
        ...props,
      };

    return {};
  }

  if (type === 'pricing') {
    if ('prices' in configByLocale)
      return {
        ...configByLocale.prices,
        ...props,
      };

    return {};
  }

  // product pages hero section
  if (isProductPageHero) {
    if ('hero' in configByLocale)
      return {
        ...configByLocale.hero,
        ...props,
      };
    return {};
  }

  if (type === 'ho_giving_back') {
    if ('bottomLogos' in configByLocale)
      return {
        ...configByLocale.bottomLogos,
        ...props,
      };
    return {};
  }

  if (type === 'ho_faqs' && 'faq' in configByLocale) {
    return {
      darkBackground: true,
      ...configByLocale.faq,
      ...props,
    };
  }

  if (type === 'life_reviews') {
    if ('trusted' in configByLocale)
      return {
        ...configByLocale.trusted,
        ...props,
      };
    return {};
  }

  if (type === 'life_flow_steps') {
    if ('coverages' in configByLocale)
      return {
        ...configByLocale.coverages,
        ...props,
      };
    return {};
  }

  if (type === 'car_overall_coverages') {
    if ('overallCoverages' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.overallCoverages,
        ...props,
      };
    return {};
  }

  if (type === 'ho_coverage') {
    if ('coverage' in configByLocale)
      return {
        ...configByLocale.coverage,
        ...props,
      };
    return {};
  }

  if (type === 'car_basic_coverages') {
    if ('basicCoverages' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.basicCoverages,
        ...props,
      };
    return {};
  }

  if (type === 'car_faq') {
    if ('faq' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.faq,
        ...props,
      };
    return {};
  }

  if (type === 'car_low_mileage') {
    if ('lowMilage' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.lowMilage,
        ...props,
      };
    return {};
  }

  if (type === 'car_low_mileage_ppm') {
    if ('lowMilagePpm' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.lowMilagePpm,
        ...props,
      };
    return {};
  }

  if (type === 'car_electric_coverages') {
    if ('electricCoverages' in fallbackConfigForCar)
      return {
        ...fallbackConfigForCar.electricCoverages,
        ...props,
      };
    return {};
  }

  // Extend LPG props: add more props based on type

  // Important! Return hero (home page) section config with props by default
  return {
    ...configByLocale.hero,
    ...props,
  };
};
